import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import {
  FvActionsheetComponent,
  FvConfirmComponent,
  FvContextMenuComponent,
  FvPromptComponent,
  FvSnackbarComponent,
} from '@discocil/fv-components';

import { NgClass, NgIf, NgStyle } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { distinctUntilChanged } from 'rxjs';
import { ChangeDeliveredModalComponent } from './shared/components/change-delivered-modal/change-delivered-modal.component';
import { FvScannerLiteComponent } from './shared/components/fv-scanner-lite/fv-scanner-lite.component';
import { QuickActionsWidgetComponent } from './shared/presentation/components/quick-actions-widget/quick-actions-widget.component';
import { SplashartLoaderComponent } from './shared/components/splashart-loader/splashart-loader.component';
import { HeaderComponent } from './shared/layout/presentation/components/header/header.component';
import { AppInitializerService } from './shared/services/app-initializer/app-initializer.service';
import { GlobalActionSheetService } from './shared/services/global-action-sheet/global-action-sheet.service';
import { GlobalConfirmService } from './shared/services/global-confirm/global-confirm.service';
import { GlobalContextMenuService } from './shared/services/global-context-menu/global-context-menu.service';
import { GlobalDynamicFvModalLoaderService } from './shared/services/global-dynamic-fv-modal-loader/global-dynamic-fv-modal-loader.service';
import { GlobalPromptService } from './shared/services/global-prompt/global-prompt.service';
import { GlobalFvScannerService } from './shared/services/global-scanner/global-scanner.service';
import { GlobalSnackbarService } from './shared/services/global-snackbar/global-snackbar.service';
import { GlobalSplashArtLoaderService } from './shared/services/global-splashart-loader/global-splashart-loader.service';
import { IsDesktopDirective } from './shared/services/layout/directives/is-desktop.directive';
import { IHeader } from './shared/services/layout/interface/header.interface';
import { LayoutService } from './shared/services/layout/layout.service';
import { Logger } from './core/interfaces/Logger';
import { FvPhysicalPaymentsBridgeService } from '@discocil/fv-angular-physical-payments-bridge';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    HeaderComponent,
    NgStyle,
    FvSnackbarComponent,
    FvPromptComponent,
    FvConfirmComponent,
    FvContextMenuComponent,
    FvActionsheetComponent,
    FvScannerLiteComponent,
    ChangeDeliveredModalComponent,
    QuickActionsWidgetComponent,
    SplashartLoaderComponent,
    NgClass,
    RouterOutlet,
    IsDesktopDirective,
  ],
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  public readonly appInitializerService = inject(AppInitializerService);
  private readonly globalSnackbarService = inject(GlobalSnackbarService);
  private readonly globalPromptService = inject(GlobalPromptService);
  private readonly globalConfirmService = inject(GlobalConfirmService);
  private readonly globalContextMenuService = inject(GlobalContextMenuService);
  private readonly logger = inject(Logger);

  private readonly globalDynamicFvModalLoaderService = inject(
    GlobalDynamicFvModalLoaderService
  );
  private readonly globalSplashArtLoaderService = inject(
    GlobalSplashArtLoaderService
  );
  private readonly globalActionSheetService = inject(GlobalActionSheetService);
  private readonly globalScannerService = inject(GlobalFvScannerService);
  private readonly layoutService = inject(LayoutService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly fvPhysicalPaymentsBridgeService = inject(FvPhysicalPaymentsBridgeService);

  globalPrompt = viewChild.required<FvPromptComponent>('prompt');
  globalConfirm = viewChild.required<FvConfirmComponent>('confirm');
  contextMenu = viewChild.required<FvContextMenuComponent>('contextMenu');
  actionSheet = viewChild.required<FvActionsheetComponent>('actionSheet');
  globalSnackbar = viewChild.required<FvSnackbarComponent>('snackbar');
  globalScanner = viewChild.required<FvScannerLiteComponent>('scanner');
  globalSplashArt = viewChild.required<SplashartLoaderComponent>('splashArt');
  globalModalContainer = viewChild.required('globalDynamicModalContainer', {
    read: ViewContainerRef,
  });

  isScreenFixed = signal<boolean>(false);
  isHeaderEnabled = signal<boolean>(false);
  headerStyle = signal<string>('');

  ngOnInit(): void {
    this.initFixedScreenLayoutListener();
    this.initHeaderLayoutListener();
    this.initPhysicalPaymentsRouter();
  }

  private initPhysicalPaymentsRouter(): void {
    (window as any).sendDataToWebview = (message: any): void => {
      this.fvPhysicalPaymentsBridgeService.receiveFromNative(message);
    }

    this.fvPhysicalPaymentsBridgeService.setSenderFunction((message: any) => {
      const webkit = (window as any).webkit;

      if (
        webkit &&
        webkit.messageHandlers &&
        webkit.messageHandlers.cordova_iab
      ) {
        webkit.messageHandlers.cordova_iab.postMessage(
          JSON.stringify({ key: 'MESSAGE_FROM_PHYSICAL_PAYMENTS', message })
        );
      }
    });
  }

  ngAfterViewInit(): void {
    this.initGlobalComponents();
    this.globalDynamicFvModalLoaderService.setViewContainerRef(
      this.globalModalContainer()
    );
  }

  private initGlobalComponents(): void {
    const componentMappings = [
      { service: this.globalPromptService, component: this.globalPrompt },
      { service: this.globalConfirmService, component: this.globalConfirm },
      { service: this.globalContextMenuService, component: this.contextMenu },
      { service: this.globalActionSheetService, component: this.actionSheet },
      { service: this.globalSnackbarService, component: this.globalSnackbar },
      { service: this.globalScannerService, component: this.globalScanner },
      {
        service: this.globalSplashArtLoaderService,
        component: this.globalSplashArt,
      },
    ];

    componentMappings.forEach(({ service, component }) => {
      service.currentComponent = component();
    });

    this.initGlobalPromptListeners();
    this.initGlobalDynamicFvModalLoaderListeners();
  }

  private initGlobalPromptListeners(): void {
    this.globalPromptService.onOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  private initGlobalDynamicFvModalLoaderListeners(): void {
    this.globalDynamicFvModalLoaderService.onDynamicModalOpened$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  /**
   * Subscribes to fixed behavior subject to know when the screen should be positioned as fixed
   *
   * @private
   * @memberof AppComponent
   */
  private initFixedScreenLayoutListener(): void {
    this.layoutService.onFixedScreenChanged$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((isScreenFixed: boolean) => {
        this.isScreenFixed.set(isScreenFixed);
      });
  }

  private initHeaderLayoutListener(): void {
    this.layoutService.onHeaderConfigChanged$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((headerConfig: IHeader | null) => {
        if (!headerConfig) {
          return;
        }

        this.isHeaderEnabled.set(headerConfig.isEnabled);
        this.headerStyle.set(headerConfig.style);
      });
  }

  /**
   * Cleanup resources when component is destroyed
   */
  ngOnDestroy(): void {
    this.logger.debug('[AppComponent] Cleaning up resources');
  }
}
